import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import type { OverlayBounds, RenderOverlay } from "@/renderer/overlayTypes";
import type { PreparedSceneFrame, VectorNetworkPreview } from "@/renderer/scene/prepareSceneFrame";

export function resolveSelectionNodeBounds(
  document: CurvaDocument,
  overlayLookupItems: Array<{ id: string; bounds: OverlayBounds }>,
  overlay: Pick<RenderOverlay, "previews">,
  nodeId: string | undefined,
  vectorPreview?: VectorNetworkPreview,
): OverlayBounds | undefined {
  if (!nodeId) {
    return undefined;
  }

  const preview = overlay.previews.find(candidate => candidate.nodeId === nodeId);
  if (preview) {
    return preview.bounds;
  }

  if (vectorPreview?.id === nodeId) {
    return vectorPreview.bounds;
  }

  return findSceneNode(document, nodeId)?.bounds ?? overlayLookupItems.find(item => item.id === nodeId)?.bounds;
}

export function selectionNodeIdsForOverlay(
  overlay: Pick<RenderOverlay, "previewSelectedNodeIds" | "hoveredNodeId" | "selectedNodeIds">,
): string[] {
  const ids = new Set<string>();
  for (const nodeId of overlay.previewSelectedNodeIds ?? []) {
    ids.add(nodeId);
  }
  if (overlay.hoveredNodeId) {
    ids.add(overlay.hoveredNodeId);
  }
  for (const nodeId of overlay.selectedNodeIds) {
    ids.add(nodeId);
  }
  return [...ids];
}

export function buildSelectionNodeBoundsMap(
  document: CurvaDocument,
  overlay: RenderOverlay,
  sceneFrame: Pick<PreparedSceneFrame, "overlayLookupItems" | "vectorPreview">,
): Record<string, OverlayBounds> {
  const lookupItems = sceneFrame.overlayLookupItems.map(item => ({ id: item.id, bounds: item.bounds }));
  const boundsById: Record<string, OverlayBounds> = {};

  for (const nodeId of selectionNodeIdsForOverlay(overlay)) {
    const bounds = resolveSelectionNodeBounds(document, lookupItems, overlay, nodeId, sceneFrame.vectorPreview);
    if (bounds) {
      boundsById[nodeId] = bounds;
    }
  }

  return boundsById;
}

export function enrichSelectionOverlayBounds(
  document: CurvaDocument,
  overlay: RenderOverlay,
  sceneFrame: PreparedSceneFrame,
): RenderOverlay {
  if (overlay.vectorEditNodeId || (overlay.measureOverlay && !overlay.devMode)) {
    return overlay;
  }

  return {
    ...overlay,
    selectionNodeBoundsById: buildSelectionNodeBoundsMap(document, overlay, sceneFrame),
  };
}
